/**
 * Question landing pages — one page per search question we want to answer
 * directly ("what language is this audio", "what language am I hearing").
 *
 * Search Console shows these queries landing on the homepage, which never
 * uses the phrasing, so they get impressions but few clicks. Each entry here
 * becomes a route (src/seo/routes.js), a prerendered HTML file, a sitemap
 * entry, and a page rendered by components/QuestionPage.js.
 *
 * Plain data only: Node imports this during the build.
 *
 *   slug        URL path segment (served at /<slug>).
 *   question    The query, phrased as the H1 and the FAQ question.
 *   answer      One-sentence direct answer — the snippet we want Google to show.
 *   lede        Hero paragraph under the H1.
 *   primary     'mic' or 'upload': which way in the hero leads with.
 *   steps       How-to steps, rendered as a list and as HowTo JSON-LD.
 *   situations  { title, text } cards for the concrete cases people search from.
 *   faq         { q, a } pairs, rendered on the page and as FAQPage JSON-LD.
 *   languages   Language slugs to link to (must exist in languages.js).
 */

export const QUESTIONS = [
  {
    slug: 'what-language-is-this',
    question: 'What language is this audio?',
    title: 'What Language Is This Audio? Identify Any Spoken Language Free | LangShazam',
    description:
      'Not sure what language a voice note, video or song is in? Play or upload the audio and LangShazam tells you the language in seconds, with a transcript. Free, in your browser.',
    answer:
      'Upload the recording or play it near your mic on LangShazam, and it names the spoken language within a few seconds and transcribes what was said.',
    lede:
      'Got a voice note, voicemail, video or clip and no idea what language it is in? Upload the file or play it near your phone, and LangShazam listens to the audio itself and tells you the language in seconds, with a transcript of what was said.',
    primary: 'upload',
    steps: [
      { name: 'Open LangShazam', text: 'Go to langshazam.com in any browser and sign in with Google or email. Nothing to install.' },
      { name: 'Upload or play the audio', text: 'Tap "Or upload a recording" and pick the file, or tap the mic and play the audio near your device.' },
      { name: 'Read the result', text: 'The detected language appears within seconds, along with a transcript in that language’s own script.' },
    ],
    situations: [
      { title: 'A voice note or voicemail', text: 'A message from a number you don’t know, or a WhatsApp voice note forwarded in a language you can’t place.' },
      { title: 'A video or reel', text: 'A YouTube, TikTok or Instagram clip with no captions. Save or screen-record it and upload the file.' },
      { title: 'A song', text: 'Lyrics you can’t make out. Sung audio works best when the vocals are clear over the music.' },
      { title: 'A podcast or radio stream', text: 'Hold your phone up to the speaker, or upload the episode — the first 10 minutes are analyzed.' },
    ],
    faq: [
      {
        q: 'How do I find out what language an audio clip is in?',
        a: 'Upload the clip to LangShazam or play it near your microphone. It listens to the speech itself and names the language within a few seconds, then shows a transcript of what was said.',
      },
      {
        q: 'What kinds of audio files can I upload?',
        a: 'Voice notes, voicemails, podcast episodes, and audio or video files from your phone or computer. The first 10 minutes are analyzed, and uploads recognise 99+ languages.',
      },
      {
        q: 'Can it tell similar languages apart?',
        a: 'Yes. Because it listens to the sound rather than guessing from text, LangShazam separates closely related pairs like Spanish and Portuguese, Hindi and Urdu, or Norwegian and Danish.',
      },
      {
        q: 'Is it free?',
        a: 'Yes. Create a free account with Google or email and start detecting. Your results, transcripts and recordings are saved to your account.',
      },
      {
        q: 'What if the audio has more than one language?',
        a: 'Every finalized phrase gets its own language tag, so a switch from one language to another mid-recording shows up as it happens.',
      },
    ],
    languages: ['spanish', 'portuguese', 'arabic', 'hindi', 'russian', 'turkish', 'korean', 'japanese'],
  },
  {
    slug: 'what-language-am-i-hearing',
    question: 'What language am I hearing?',
    title: 'What Language Am I Hearing? Identify Spoken Languages Live | LangShazam',
    description:
      'Hearing a language you can’t place in a café, on the street or on TV? Tap the mic and LangShazam names the language in seconds and transcribes it live. Free, no app.',
    answer:
      'Tap the mic on LangShazam and hold your phone toward the speaker; within a few seconds it names the language you’re hearing and starts a live transcript.',
    lede:
      'People speaking near you on the train, a TV in the next room, a busker, a phone call across the café — and you can’t tell what language it is. Tap the mic, point your phone toward the sound, and LangShazam tells you within seconds, with a live transcript.',
    primary: 'mic',
    steps: [
      { name: 'Open LangShazam', text: 'Go to langshazam.com in your phone’s browser and sign in with Google or email. No app to install.' },
      { name: 'Tap the mic', text: 'Allow microphone access and point your phone toward whoever is speaking.' },
      { name: 'See the language', text: 'After a few seconds of speech, the language appears on screen and the transcript keeps updating while you listen.' },
    ],
    situations: [
      { title: 'Around you in public', text: 'A conversation on the bus, at the next table, or in a shop. Detection runs quietly on your phone without interrupting anyone.' },
      { title: 'On TV or radio', text: 'A channel, film or station you landed on by accident. Hold your phone near the speaker.' },
      { title: 'While travelling', text: 'Announcements, guides and locals in a region where several languages are spoken side by side.' },
      { title: 'On a call or video', text: 'Someone switched languages on a call or in a livestream. Detection picks up the switch mid-sentence.' },
    ],
    faq: [
      {
        q: 'How can I tell what language someone is speaking?',
        a: 'Open LangShazam, tap the mic and hold your phone toward the speaker. After a few seconds of clear speech it names the language and shows a live transcript.',
      },
      {
        q: 'How long do I need to listen for?',
        a: 'Usually a few seconds of continuous speech. Short, quiet or very noisy audio is harder, so get as close to the speaker as you comfortably can.',
      },
      {
        q: 'Does it work on my phone?',
        a: 'Yes. It runs in Safari, Chrome and Firefox on iPhone and Android, with no app to download.',
      },
      {
        q: 'Is there a Shazam for languages?',
        a: 'Yes — that is exactly what LangShazam is. Shazam names a song from a few seconds of audio; LangShazam names the spoken language and transcribes it.',
      },
      {
        q: 'Is it free?',
        a: 'Yes. Sign up free with Google or email. Each session is saved to your account with the detected language, transcript and a replayable recording.',
      },
    ],
    languages: ['english', 'spanish', 'french', 'german', 'italian', 'chinese', 'cantonese', 'vietnamese'],
  },
];

export const QUESTION_BY_SLUG = Object.fromEntries(QUESTIONS.map((q) => [q.slug, q]));
